import React from 'react';
import { AlertTriangle, X } from 'lucide-react';
import Button from '@components/common/Button';
import Textarea from '@components/common/Textarea';
import Message from '@components/common/Message';
import dialogStyles from '@components/common/ConfirmDialog.module.css';
import {
  AssessmentStatus,
  CANCELLATION_REASON_MAX_LENGTH,
  CANCELLATION_REASON_MIN_LENGTH
} from '@app-types';
import styles from './CancelAssessmentDialog.module.css';

export interface CancelAssessmentDialogProps {
  status: AssessmentStatus;
  isPending: boolean;
  /** Error returned by the server for the last attempt; shown inside the dialog. */
  serverError: string | null;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}

/** Returns the validation message for a reason, or null when it is valid. */
export const validateCancellationReason = (reason: string): string | null => {
  const length = reason.trim().length;
  if (length === 0) return 'Enter a reason for cancelling.';
  if (length < CANCELLATION_REASON_MIN_LENGTH) {
    return `The reason must be at least ${CANCELLATION_REASON_MIN_LENGTH} characters.`;
  }
  return null;
};

/**
 * Asks a Program Lead or Admin for the reason before cancelling a published
 * assessment. Stays open while the request is pending and on a server error,
 * so the typed reason is never lost.
 */
const CancelAssessmentDialog: React.FC<CancelAssessmentDialogProps> = ({
  status,
  isPending,
  serverError,
  onConfirm,
  onClose
}) => {
  const titleId = React.useId();
  const [reason, setReason] = React.useState('');
  const [touched, setTouched] = React.useState(false);
  const [submitAttempted, setSubmitAttempted] = React.useState(false);

  const trimmedLength = reason.trim().length;
  const validationError = validateCancellationReason(reason);
  const showError = (touched || submitAttempted) && validationError !== null;

  const handleClose = () => {
    if (!isPending) onClose();
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitAttempted(true);
    if (validationError || isPending) return;
    onConfirm(reason.trim());
  };

  return (
    <div className={dialogStyles.confirmDialogOverlay}>
      <button
        type="button"
        className={dialogStyles.confirmDialogOverlayBackdrop}
        onClick={handleClose}
        tabIndex={-1}
        aria-hidden="true"
      />
      <form
        className={dialogStyles.confirmDialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-test-id="cancel-assessment-reason-dialog"
        onSubmit={handleSubmit}
        noValidate
      >
        <div className={dialogStyles.confirmDialogHeader}>
          <div className={dialogStyles.confirmDialogIcon}>
            <AlertTriangle size={24} />
          </div>
          <h3 id={titleId} className={dialogStyles.confirmDialogTitle}>
            Cancel published assessment?
          </h3>
          <button
            type="button"
            className={dialogStyles.confirmDialogClose}
            onClick={handleClose}
            disabled={isPending}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className={dialogStyles.confirmDialogContent}>
          <p className={dialogStyles.confirmDialogMessage}>
            This assessment is {status} and the supplier has already been asked for evidence. Cancelling cannot be
            undone. The Contract Manager, Contract Owner and Assessor will be emailed your reason.
          </p>

          <Textarea
            label="Reason for cancelling"
            required
            rows={4}
            value={reason}
            maxLength={CANCELLATION_REASON_MAX_LENGTH}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            error={showError ? validationError : undefined}
            disabled={isPending}
            wrapperClassName={styles.field}
            dataTestId="cancel-reason"
            data-test-id="cancel-reason-input"
          />
          <div className={styles.counter} data-test-id="cancel-reason-counter" aria-live="polite">
            {trimmedLength} / {CANCELLATION_REASON_MAX_LENGTH}
            {trimmedLength < CANCELLATION_REASON_MIN_LENGTH && ` (min ${CANCELLATION_REASON_MIN_LENGTH})`}
          </div>

          {serverError && (
            <Message variant="error" className={styles.serverError} data-test-id="cancel-assessment-server-error">
              <span>{serverError}</span>
            </Message>
          )}
        </div>

        <div className={dialogStyles.confirmDialogFooter}>
          <Button variant="ghost" onClick={handleClose} disabled={isPending}>
            Go Back
          </Button>
          <Button
            type="submit"
            variant="danger"
            disabled={validationError !== null || isPending}
            dataTestId="cancel-assessment-confirm"
          >
            {isPending ? 'Cancelling...' : 'Cancel Assessment'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CancelAssessmentDialog;
